import { type FC, forwardRef, type HTMLAttributes, type ReactNode } from 'react'
import { CheckIcon, XCloseIcon } from '@/components/ui/icons'
import { useTranslation } from '@/translations/i18n'

export const Title = (props: HTMLAttributes<HTMLHeadElement>) => (
	<h1 className='font-medium text-display-xs' {...props} />
)

export const SectionTitle = (props: HTMLAttributes<HTMLParagraphElement>) => (
	<p className='font-medium text-lg' {...props} />
)

export const NoneValue = (props: HTMLAttributes<HTMLParagraphElement>) => (
	<p className='text-neutral-500' {...props} />
)

type ContentProps = HTMLAttributes<HTMLDivElement> & {
	gap?: number
	title?: string
	sectionTitle?: string
	columns?: number
	margin?: number
}
export const Content = forwardRef<HTMLDivElement, ContentProps>(
	(props, ref) => {
		const {
			children,
			gap = 4,
			margin = 4,
			title,
			sectionTitle,
			columns = 4,
			...rest
		} = props
		return (
			<main
				className='flex w-full flex-col'
				style={{ gap: `${gap * 4}px` }}
				{...rest}
				ref={ref}
			>
				{title && <Title>{title}</Title>}
				{sectionTitle && <SectionTitle>{sectionTitle}</SectionTitle>}
				<div
					className='grid w-full max-w-full'
					style={{
						gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
						gap: `${gap * 4}px`,
						margin: `${margin * 4}px 0`
					}}
				>
					{children}
				</div>
			</main>
		)
	}
)

const FieldContent = ({ children }: { children: ReactNode }) => (
	<ul className='w-full [&_li]:ml-1.5 [&_li]:w-full [&_li]:overflow-hidden [&_li]:text-ellipsis [&_li]:whitespace-nowrap [&_li]:before:mr-1 [&_li]:before:content-["•"] [&_li_li]:ml-2 [&_span]:text-neutral-600 [&_span]:text-sm'>
		{children}
	</ul>
)

interface AnswerFieldProps {
	labelTitle: ReactNode
	value?: string | number
	hasValue?: boolean
	children?: ReactNode
}
export const AnswerField: FC<AnswerFieldProps> = ({
	labelTitle,
	value,
	children,
	hasValue = true
}) => {
	const { t } = useTranslation()
	return (
		<div className='flex w-full flex-col gap-1.5'>
			{labelTitle && (
				<h3 className='font-medium text-neutral-600 text-sm'>
					{labelTitle}
				</h3>
			)}

			{hasValue ? (
				<FieldContent>{children ?? value}</FieldContent>
			) : (
				<NoneValue>{t('common:none')}</NoneValue>
			)}
		</div>
	)
}

interface YesNoAnswerFieldProps extends AnswerFieldProps {
	yesNoValue?: boolean
	children?: ReactNode
}
export const YesNoAnswerField: FC<YesNoAnswerFieldProps> = ({
	labelTitle,
	yesNoValue,
	hasValue = yesNoValue !== undefined,
	children
}) => {
	const { t } = useTranslation()
	return (
		<AnswerField hasValue={hasValue} labelTitle={labelTitle}>
			{yesNoValue ? (
				<div className='flex items-center gap-1'>
					<CheckIcon className='fill-neutral-900' /> {t('common:yes')}
				</div>
			) : (
				<div className='flex items-center gap-1'>
					<XCloseIcon className='fill-neutral-900' /> {t('common:no')}
				</div>
			)}
			{children && <FieldContent>{children}</FieldContent>}
		</AnswerField>
	)
}
